import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getDsokCustomerDetailByUrl } from "./dsokCustomerDetailApi";
import { DsokCustomerDetailDataContext } from "./DsokCustomerDetailContext";
import LoadingView from "../views/shared/LoadingView";
import CustomerDetailErrorView from "../views/shared/CustomerDetailErrorView";

const DsokCustomerDetailProvider = ({ children }) => {
  const { customerUrl, customerId } = useParams();
  const effectiveCustomerUrl = customerUrl ?? customerId;
  const [isDirty, setDirty] = useState(false);
  const [originalDsokSettings, setOriginalDsokSettingsState] = useState(null);
  const sectionPayloadGetters = useRef({});
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });

  const resetDirty = useCallback(() => setDirty(false), []);

  const setOriginalDsokSettings = useCallback((next) => {
    setOriginalDsokSettingsState(next);
  }, []);

  const registerSectionUpdatePayload = useCallback((sectionId, getPayload) => {
    sectionPayloadGetters.current[sectionId] = getPayload;
  }, []);

  const unregisterSectionUpdatePayload = useCallback((sectionId) => {
    delete sectionPayloadGetters.current[sectionId];
  }, []);

  const getSectionUpdatePayload = useCallback((sectionId) => {
    const getter = sectionPayloadGetters.current[sectionId];
    return getter ? getter() : null;
  }, []);

  const fetchData = useCallback(async () => {
    if (!effectiveCustomerUrl) {
      setState({ data: null, loading: false, error: null });
      setOriginalDsokSettingsState(null);
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await getDsokCustomerDetailByUrl(effectiveCustomerUrl);
      setState({ data, loading: false, error: null });
      setOriginalDsokSettingsState(data?.dsokSettings ?? null);
    } catch (err) {
      setState({
        data: null,
        loading: false,
        error: err?.message || "Failed to load customer data",
      });
      setOriginalDsokSettingsState(null);
    }
  }, [effectiveCustomerUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const value = {
    customerUrl: effectiveCustomerUrl,
    data: state.data,
    loading: state.loading,
    error: state.error,
    refetch: fetchData,
    isDirty,
    setDirty,
    resetDirty,
    originalDsokSettings,
    setOriginalDsokSettings,
    registerSectionUpdatePayload,
    unregisterSectionUpdatePayload,
    getSectionUpdatePayload,
  };

  if (state.loading) {
    return <LoadingView />;
  }

  if (state.error) {
    return (
      <CustomerDetailErrorView
        message={state.error}
        onRetry={fetchData}
      />
    );
  }

  return (
    <DsokCustomerDetailDataContext.Provider value={value}>
      {children}
    </DsokCustomerDetailDataContext.Provider>
  );
};

export default DsokCustomerDetailProvider;
